import { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contextos/AuthContext';
import Logo from '../images/Logo';
import NovaTransacaoSheet from '../telas/movimentacoes/NovaTransacaoSheet';
import Avatar from './Avatar';
import Button from './Button';
import Icon from './Icon';
import Sheet from './Sheet';
import styles from './AppShell.module.css';

export type AppShellProps = {
  titulo: string;
  subtitulo?: string;
  acoes?: ReactNode;
  children: ReactNode;
};

type ItemNav = {
  rotulo: string;
  caminho: string;
  icone: string;
};

type GrupoNav = { rotulo: string; itens: ItemNav[] };

// Sidebar agrupada por contexto de uso — o dia a dia em cima, estrutura no
// meio, utilitários no fim. O rótulo do grupo dá o respiro visual entre blocos.
const GRUPOS: GrupoNav[] = [
  {
    rotulo: 'Visão geral',
    itens: [
      { rotulo: 'Dashboard', caminho: '/dashboard', icone: 'dashboard' },
      { rotulo: 'Movimentações', caminho: '/movimentacoes', icone: 'currency_exchange' },
      { rotulo: 'Metas', caminho: '/metas', icone: 'flag' }
    ]
  },
  {
    rotulo: 'Cadastros',
    itens: [
      { rotulo: 'Contas', caminho: '/contas', icone: 'assured_workload' },
      { rotulo: 'Cartões', caminho: '/cartoes', icone: 'credit_card' },
      { rotulo: 'Categorias', caminho: '/categorias', icone: 'category' }
    ]
  },
  {
    rotulo: 'Ferramentas',
    itens: [
      { rotulo: 'Importar', caminho: '/importar', icone: 'upload_file' },
      { rotulo: 'Configurações', caminho: '/configuracoes', icone: 'settings' }
    ]
  }
];

const NAV = GRUPOS.flatMap((grupo) => grupo.itens);

// Bottom-bar mobile: Dashboard, Movimentações, [+], Metas, Mais.
const NAV_BARRA = [NAV[0], NAV[1]];
const NAV_BARRA_DEPOIS = [NAV[2]];
// Itens que ficam no sheet "Mais" no mobile.
const NAV_MAIS = NAV.slice(3);

export default function AppShell({ titulo, subtitulo, acoes, children }: AppShellProps) {
  const [novaAberta, setNovaAberta] = useState(false);
  const [maisAberto, setMaisAberto] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario, sair } = useAuth();

  const estaAtivo = (caminho: string) =>
    location.pathname === caminho || location.pathname.startsWith(`${caminho}/`);

  const irPara = (caminho: string) => {
    setMaisAberto(false);
    navigate(caminho);
  };

  const handleSair = () => {
    setMaisAberto(false);
    sair();
    navigate('/');
  };

  const itemNav = (item: ItemNav) => (
    <button
      key={item.caminho}
      type="button"
      className={`${styles.navItem} ${estaAtivo(item.caminho) ? styles.navItemAtivo : ''}`}
      onClick={() => irPara(item.caminho)}
    >
      <Icon nome={item.icone} tamanho={20} />
      <span>{item.rotulo}</span>
    </button>
  );

  const itemBarra = (item: ItemNav) => (
    <button
      key={item.caminho}
      type="button"
      className={`${styles.itemBarra} ${estaAtivo(item.caminho) ? styles.itemBarraAtivo : ''}`}
      onClick={() => irPara(item.caminho)}
    >
      <Icon nome={item.icone} tamanho={22} />
      <span>{item.rotulo}</span>
    </button>
  );

  return (
    <>
      {/* Sidebar desktop */}
      <aside className={styles.sidebar}>
        <div className={styles.marca}>
          <Logo className={styles.logo} />
          <span className={styles.nomeApp}>Finant</span>
        </div>

        <div className={styles.novaTransacao}>
          <Button variante="primaria" icone="add" onClick={() => setNovaAberta(true)}>
            Nova transação
          </Button>
        </div>

        <nav className={styles.nav}>
          {GRUPOS.map((grupo) => (
            <div key={grupo.rotulo} className={styles.grupo}>
              <span className={styles.grupoRotulo}>{grupo.rotulo}</span>
              {grupo.itens.map(itemNav)}
            </div>
          ))}
        </nav>

        <div className={styles.rodapeSidebar}>
          <div className={styles.usuario} onClick={() => navigate('/configuracoes')}>
            <Avatar nome={usuario?.nome ?? usuario?.email} tamanho={36} />
            <div className={styles.usuarioInfo}>
              <span className={styles.usuarioNome}>{usuario?.nome ?? 'Minha conta'}</span>
              <span className={styles.usuarioEmail}>{usuario?.email}</span>
            </div>
          </div>
          <button type="button" className={styles.botaoSair} onClick={handleSair} title="Sair">
            <Icon nome="logout" tamanho={18} />
          </button>
        </div>
      </aside>

      {/* Conteúdo */}
      <main className={styles.conteudo}>
        <div className={styles.interno}>
          <header className={styles.cabecalhoPagina}>
            <div className={styles.cabecalhoTextos}>
              <h1 className={styles.tituloPagina}>{titulo}</h1>
              {subtitulo && <p className={styles.subtituloPagina}>{subtitulo}</p>}
            </div>
            {acoes && <div className={styles.acoes}>{acoes}</div>}
          </header>
          {children}
        </div>
      </main>

      {/* Bottom-bar mobile */}
      <nav className={styles.barraInferior}>
        {NAV_BARRA.map(itemBarra)}
        <button
          type="button"
          className={styles.botaoCentral}
          onClick={() => setNovaAberta(true)}
          aria-label="Nova transação"
        >
          <Icon nome="add" tamanho={26} />
        </button>
        {NAV_BARRA_DEPOIS.map(itemBarra)}
        <button
          type="button"
          className={`${styles.itemBarra} ${NAV_MAIS.some((item) => estaAtivo(item.caminho)) ? styles.itemBarraAtivo : ''}`}
          onClick={() => setMaisAberto(true)}
        >
          <Icon nome="apps" tamanho={22} />
          <span>Mais</span>
        </button>
      </nav>

      {/* Sheet "Mais" (mobile) */}
      <Sheet aberto={maisAberto} onFechar={() => setMaisAberto(false)} titulo="Mais">
        <div className={styles.maisLista}>
          {NAV_MAIS.map(itemNav)}
          <button type="button" className={`${styles.navItem} ${styles.sairItem}`} onClick={handleSair}>
            <Icon nome="logout" tamanho={20} />
            <span>Sair</span>
          </button>
        </div>
      </Sheet>

      <NovaTransacaoSheet aberto={novaAberta} onFechar={() => setNovaAberta(false)} />
    </>
  );
}
